import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import TopMenuBar from './TopMenuBar';
import Sidebar from './Sidebar';
import IconRail from './IconRail';
import { useApp } from '../../context/AppContext';

/**
 * The application shell.
 *
 * On a desktop the icon rail and the text panel sit beside the page, as they always
 * have. On a phone they cannot: 60px of rail plus about 220 of panel leaves roughly a
 * hundred pixels of a 375px screen for the actual work. Below the lg breakpoint they
 * come out of the layout flow entirely and slide over the content as one drawer,
 * opened from the button in the top bar and closed by the backdrop, the Escape key or
 * simply going somewhere.
 *
 * The same two components serve both, rather than a second mobile-only navigation:
 * one that drifts out of step with the real menu is worse than none.
 */
export default function Layout({ children }) {
  const { state, dispatch } = useApp();
  const location = useLocation();
  const navOpen = state.mobileNavOpen;

  // Choosing a page is the end of navigating, so the drawer closes itself. Without
  // this it would stay over the page the person just asked for.
  //
  // It watches for the path actually changing rather than simply for the drawer being
  // open: an effect that closes whenever navOpen is true runs the moment it is set and
  // shuts the drawer before anyone sees it.
  const lastPath = useRef(location.pathname);
  useEffect(() => {
    if (lastPath.current === location.pathname) return;
    lastPath.current = location.pathname;
    dispatch({ type: 'CLOSE_MOBILE_NAV' });
  }, [location.pathname, dispatch]);

  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') dispatch({ type: 'CLOSE_MOBILE_NAV' }); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen, dispatch]);

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-sap-bg dark:bg-slate-950 transition-colors">
      <div className="flex flex-1 overflow-hidden">
        {/*
          Below lg this whole group is lifted out of the flow and slid off to the left,
          so the page gets the full width. It is still in the DOM, so the open and close
          animate and the menu keeps its scroll position.
        */}
        {/*
          Above the header below lg, not merely above the page.

          The header carries four z-50s of its own and comes later in the document, so
          at an equal z-index the menu row and the account controls painted straight
          through an open drawer. On a desktop the two sit side by side and never
          overlap, so the raise is scoped to the width where they do.
        */}
        <div
          className={`flex shrink-0 z-50 max-lg:z-[100] max-lg:fixed max-lg:inset-y-0 max-lg:left-0
                      max-lg:transition-transform max-lg:duration-200 max-lg:shadow-2xl
                      ${navOpen ? 'max-lg:translate-x-0' : 'max-lg:-translate-x-full'}`}
          aria-hidden={navOpen ? undefined : true}
        >
          <IconRail />
          {!state.sidebarCollapsed && <Sidebar />}

          {/*
            An explicit way out. Once the drawer is open it covers the hamburger that
            opened it, and the strip of backdrop left beside it on a narrow phone is
            only a few dozen pixels wide to aim at.
          */}
          {navOpen && (
            <button
              type="button"
              onClick={() => dispatch({ type: 'CLOSE_MOBILE_NAV' })}
              aria-label="Close navigation"
              className="lg:hidden absolute top-2 right-2 p-1.5 rounded-lg bg-white/90 text-sap-text
                         shadow ring-1 ring-black/5 dark:bg-slate-800/90 dark:text-slate-100"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="2" strokeLinecap="round" aria-hidden>
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Tapping away from the drawer is the usual way to dismiss it. */}
        {navOpen && (
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => dispatch({ type: 'CLOSE_MOBILE_NAV' })}
            className="lg:hidden fixed inset-0 z-[90] bg-black/40"
          />
        )}

        {/* Main content column */}
        <div className="flex flex-col flex-1 overflow-hidden min-w-0">
          <TopMenuBar />

          <main className="flex-1 overflow-auto bg-sap-bg dark:bg-slate-950 transition-colors">
            {/* Breadcrumb */}
            {state.breadcrumb.length > 0 && (
              <div className="h-8 px-3 sm:px-6 flex items-center gap-0 overflow-x-auto no-scrollbar bg-white dark:bg-slate-900 border-b border-sap-border dark:border-slate-700 text-xs text-sap-textMuted dark:text-slate-400 select-none">
                {state.breadcrumb.map((crumb, i) => (
                  <span key={i} className="flex items-center whitespace-nowrap">
                    {i > 0 && <span className="mx-2 text-gray-300">/</span>}
                    <span className={i === state.breadcrumb.length - 1 ? 'text-sap-text dark:text-slate-200 font-medium' : ''}>
                      {crumb}
                    </span>
                  </span>
                ))}
              </div>
            )}

            {/* Page content. The gutter tightens on a phone, where six units of padding
                on each side is a sixth of the screen. */}
            <div className="p-3 sm:p-6">
              {children}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
